import React from 'react';
import {
    Datagrid,
    DateField,
    FunctionField,
    List,
    Pagination,
    SelectInput,
    ShowButton,
    TextField,
    TextInput,
    DateInput,
    useListContext,
} from 'react-admin';
import { HandoverRecord, handoverStatusChoices, handoverRowStyle, isTimeoutWarning, renderStationShortId, renderSlotShortId } from './handover-status';


const srOnlyStyle: React.CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
};

const getDefaultCreatedDateFrom = (): string => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
};

// Modul-Konstante — außerhalb Komponente für Stabilität (Performance AC)
const handoverFilters = [
    <SelectInput
        key="status"
        label="Status"
        source="status"
        choices={handoverStatusChoices}
        alwaysOn
        emptyText="Alle Status"
        resettable
    />,
    <TextInput
        key="stationId"
        label="Station-ID"
        source="stationId"
        alwaysOn
    />,
    <DateInput
        key="createdDateFrom"
        label="Erstellt von"
        source="createdDateFrom"
    />,
    <DateInput
        key="createdDateTo"
        label="Erstellt bis"
        source="createdDateTo"
    />,
];

// Modul-Konstante — kein inline-render in JSX (Performance AC)
const renderStatusCell = (record: HandoverRecord): React.ReactNode => {
    const label = handoverStatusChoices.find(c => c.id === record?.status)?.name ?? record?.status;
    const showTimeout = record?.expiresAt && isTimeoutWarning(record.expiresAt, record.status);
    return (
        <span>
            {label}
            {showTimeout && (
                <span aria-label="Timeout-Warnung: läuft bald ab"> ⏱ läuft bald ab</span>
            )}
        </span>
    );
};

// aria-live Ankündigung nach Filter-Anwendung (WCAG 4.1.3)
const HandoverResultAnnouncer: React.FC = () => {
    const { total, isLoading } = useListContext();
    return (
        <div aria-live="polite" aria-atomic="true" style={srOnlyStyle}>
            {!isLoading && total !== undefined && `${total} Handovers gefunden`}
        </div>
    );
};

const uuidCellSx = { maxWidth: '9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const HandoverListContent: React.FC = () => (
    <>
        <HandoverResultAnnouncer />
        <Datagrid
            rowStyle={handoverRowStyle}
            aria-label="Handover-Liste"
            bulkActionButtons={false}
            rowClick="show"
            sx={{
                '& .column-id': uuidCellSx,
            }}
        >
            <TextField source="id" label="ID" />
            <FunctionField label="Status" render={renderStatusCell} />
            <FunctionField source="stationShortId" label="Station" render={renderStationShortId} />
            <FunctionField source="slotShortId" label="Slot" render={renderSlotShortId} />
            <DateField source="createdDate" label="Erstellt" showTime />
            <DateField source="expiresAt" label="Läuft ab" showTime />
            <ShowButton label="Details" />
        </Datagrid>
    </>
);

const HandoverPagination: React.FC = () => (
    <Pagination rowsPerPageOptions={[10, 20, 50]} />
);

export const HandoverList: React.FC = () => (
    <List
        sort={{ field: 'createdDate', order: 'DESC' }}
        perPage={20}
        pagination={<HandoverPagination />}
        exporter={false}
        filters={handoverFilters}
        filterDefaultValues={{ createdDateFrom: getDefaultCreatedDateFrom() }}
        debounce={500}
    >
        <HandoverListContent />
    </List>
);
